import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import { setTimeToBeDefined } from '../../../../ts-utils/date-utils'
import { wallClockInZone } from '../../../../ts-utils/luxon-permanent-dst'
import { DEFAULT_GAME_TYPE_ID } from '../constants'
import type { GameFormMode, GameFormValues, GamePayload } from '../types'
import { buildPlaceholderVisitorPayload } from '../../placeholderVisitor/buildPlaceholderVisitorPayload'

// Registered here rather than relying on another module in the bundle having
// done it first, so this file works standalone -- in a unit test, anywhere.
dayjs.extend(utcPlugin)

interface BuildOptions {
  mode: GameFormMode
  /**
   * Tournament bracket slot ids. On edit, a submitted team id equal to its own
   * slot id is dropped from the payload so the PUT cannot overwrite a bracket
   * slot with itself. Ported from `pages/Game/index.jsx:587-599`.
   */
  homeTeamSlotId?: string | null
  visitingTeamSlotId?: string | null
}

/**
 * The start instant to publish, and the end derived from it.
 *
 * Two behaviors are preserved exactly from the legacy paths:
 *
 * 1. **Create re-zones; edit does not.** The picker yields a wall-clock
 *    reading. Create reinterprets that same reading as being in the venue's
 *    zone (`keepLocalTime`) before converting to UTC, so 09:42 chosen for a
 *    Vancouver venue posts as 09:42 Vancouver, not 09:42 UTC. Edit never did
 *    this; adding it would silently shift every edited game.
 *
 * 2. **`ends_at` is derived from whatever start is actually published.** When
 *    TBD is on that is the sentinel, not the real picked time, so the duration
 *    stays consistent with the start the API stores.
 */
const resolveInstants = (
  values: GameFormValues,
  mode: GameFormMode
): { starts_at?: string; ends_at?: string } => {
  const { startsAt, durationMinutes, timeToBeDefined, locationTimezone } =
    values

  /*
   * No usable start -- omit both date keys and let the API reject the body.
   *
   * `dayjs('').toISOString()` throws a RangeError rather than returning
   * something falsy, so without this guard submitting an empty form blows up
   * here and the request is never sent: the user sees "Invalid time value"
   * instead of the API's own "field is required" message. Validation is the
   * API's job, so this builder's contract is to always produce a body that can
   * be POSTed -- even one the API will refuse.
   */
  const parsedStart = dayjs(startsAt)
  if (!startsAt || !parsedStart.isValid()) {
    return {}
  }

  let startIso: string
  if (mode === 'create' && locationTimezone) {
    // Luxon returns null for an invalid zone/date; fall back to the plain
    // instant, which is already known valid by the guard above.
    //  Through `wallClockInZone` rather than luxon directly: for a venue in a
    //  province that abolished its clock change, a runtime whose timezone
    //  database still carries the old rule converts the typed reading on the
    //  wrong offset and stores an hour that nobody entered.
    startIso =
      wallClockInZone(startsAt, locationTimezone).toUTC().toISO() ??
      parsedStart.toISOString()
  } else {
    startIso = parsedStart.toISOString()
  }

  const publishedStart = timeToBeDefined
    ? setTimeToBeDefined(startIso)
    : startIso

  /*
   * No duration -- send the start alone and let the API report `ends_at` as
   * missing. Defaulting to 0 would post `ends_at === starts_at`, a zero-length
   * game the API may well accept, silently saving something the admin never
   * chose.
   */
  if (durationMinutes === null || durationMinutes === undefined) {
    return { starts_at: publishedStart }
  }

  const endIso = dayjs(publishedStart)
    .add(durationMinutes, 'minute')
    .utc()
    .toISOString()

  return { starts_at: publishedStart, ends_at: endIso }
}

/**
 * Form values to API body.
 *
 * Ported from the two legacy submit paths -- `sagas/game.js:76-95` (edit) and
 * `pages/Schedule/index.jsx:492-548` (create) -- keeping their asymmetries
 * intentionally rather than unifying them:
 *
 * - `game_type_id` backfills to 2 on create only; edit sends it only when the
 *   admin actually chose one.
 * - `is_upcoming_livestream` is always present on create; on edit it rides
 *   along only if the toggle wrote it.
 * - `time_to_be_defined_status` is never sent by either path. TBD lives purely
 *   in the 23:58:58 UTC sentinel on `starts_at`.
 */
export const buildGamePayload = (
  values: GameFormValues,
  { mode, homeTeamSlotId, visitingTeamSlotId }: BuildOptions
): GamePayload => {
  const isCreate = mode === 'create'
  const instants = resolveInstants(values, mode)

  const payload: GamePayload = {
    // Spread rather than assigned: with no usable start these keys are absent
    // entirely, so the API reports them missing instead of parsing an
    // `undefined`.
    ...instants,
    home_team_id: values.homeTeamId,
    /*
     * The visiting half comes from the shared builder so the two keys stay
     * mutually exclusive: a placeholder game sends a null team id, an ordinary
     * one sends a null name. Web and mobile share this, so neither can drift
     * from the other (SN-8410).
     */
    ...buildPlaceholderVisitorPayload({
      usePlaceholderVisitor: values.usePlaceholderVisitor,
      visitingTeamId: values.visitingTeamId,
      placeholderName: values.visitingTeamPlaceholder,
    }),
    // Forced rather than omitted: clearing the location has to actually clear
    // it, and an absent key would leave the old venue in place.
    venue_id: values.venueId ?? null,
    facility_id: values.facilityId ?? null,
    league_game_number: values.gameNumber || null,
    if_necessary_status: values.ifNecessaryStatus,
  }

  if (values.scheduleId) {
    payload.schedule_id = values.scheduleId
  }

  if (isCreate) {
    payload.game_type_id = values.gameTypeId
      ? parseInt(String(values.gameTypeId), 10)
      : DEFAULT_GAME_TYPE_ID
    payload.is_upcoming_livestream = values.isUpcomingLivestream || false
  } else {
    if (values.gameTypeId !== null && values.gameTypeId !== undefined) {
      payload.game_type_id = parseInt(String(values.gameTypeId), 10)
    }
    if (values.isUpcomingLivestream !== undefined) {
      payload.is_upcoming_livestream = values.isUpcomingLivestream
    }
  }

  if (!isCreate) {
    if (homeTeamSlotId && homeTeamSlotId === values.homeTeamId) {
      delete payload.home_team_id
    }
    if (visitingTeamSlotId && visitingTeamSlotId === values.visitingTeamId) {
      delete payload.visiting_team_id
    }
  }

  return payload
}
